import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useOutletContext, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Plus, X, Phone } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";
import api from "../lib/api";

// Components
import {
  SendButtonLogo,
  TypingIndicator,
  MessageItem,
  EmptyState,
  AttachmentMenu,
  ImagePreview,
  FilePreview,
  AssistantIndicator,
  VoiceCallScreen,
  removeMarkdown
} from "../components/chat";

// Hooks
import { useAttachments, useChat } from "../hooks";

export default function ChatPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { sidebarCollapsed } = useOutletContext() || {};
  const { t, isRTL } = useLanguage();
  
  // State
  const [input, setInput] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  
  // Voice Call State
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceSpeaking, setVoiceSpeaking] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const voiceMediaRecorderRef = useRef(null);
  const voiceAudioRef = useRef(null);

  // Chat & Attachments Hooks
  const chat = useChat({ sessionId, t, isRTL });
  const attachments = useAttachments({ t });

  // ============ Voice Recording (Simple) ============
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        
        if (blob.size < 1000) {
          toast.error("لم يتم تسجيل صوت كافٍ");
          return;
        }
        
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        
        try {
          toast.info("جاري تحليل الصوت...");
          const response = await api.post('/speech/transcribe', formData);
          const text = response.data.text?.trim();
          
          // Filter Whisper hallucinations
          const hallucinations = [
            "اشتركوا بالقناة", "اشتركوا في القناة", "اشترك بالقناة",
            "شكراً للمشاهدة", "شكرا للمشاهدة", "subscribe", "thanks for watching"
          ];
          const isHallucination = hallucinations.some(h => 
            text?.toLowerCase().includes(h.toLowerCase())
          );
          
          if (text && !isHallucination) {
            setInput(prev => prev ? prev + ' ' + text : text);
            toast.success("تم تحويل الصوت بنجاح");
          } else {
            toast.error("لم يتم التعرف على كلام واضح");
          }
        } catch {
          toast.error("خطأ في تحليل الصوت");
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success("جاري التسجيل... اضغط مرة أخرى للإيقاف");
    } catch {
      toast.error("لا يمكن الوصول للميكروفون");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const toggleRecording = useCallback(() => {
    isRecording ? stopRecording() : startRecording();
  }, [isRecording, startRecording, stopRecording]);

  // ============ Voice Call System ============
  
  // Pre-create AudioContext on user interaction to enable autoplay
  const audioContextRef = useRef(null);
  
  const ensureAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);
  
  const playAudioBase64 = useCallback(async (base64Audio) => {
    try {
      const ctx = ensureAudioContext();
      
      // Decode base64 to ArrayBuffer
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Decode audio data
      const audioBuffer = await ctx.decodeAudioData(bytes.buffer);
      
      // Create and play source
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      
      return new Promise((resolve) => {
        source.onended = resolve;
        source.start(0);
      });
    } catch (err) {
      console.error("AudioContext play error:", err);
      // Fallback to Audio element
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audio.volume = 1;
      await audio.play();
      return new Promise(resolve => { audio.onended = resolve; });
    }
  }, [ensureAudioContext]);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startVoiceRecording = useCallback(async () => {
    if (!showVoiceCall) return;
    
    // Ensure audio context is ready
    ensureAudioContext();
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      
      const mediaRecorder = new MediaRecorder(stream);
      voiceMediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        
        if (blob.size < 1500) {
          setVoiceRecording(false);
          return;
        }
        
        setVoiceRecording(false);
        setVoiceProcessing(true);
        
        try {
          // 1. Transcribe
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          const { data: transcribeData } = await api.post('/speech/transcribe', formData);
          const userText = transcribeData.text?.trim();
          
          // Filter hallucinations
          const badTexts = ["اشتركوا", "subscribe", "شكرا للمشاهدة", "thanks for watching"];
          if (!userText || badTexts.some(t => userText.toLowerCase().includes(t.toLowerCase()))) {
            setVoiceProcessing(false);
            return;
          }
          
          // 2. Get AI Response
          const { data: chatData } = await api.post('/chat', { 
            message: userText, 
            session_id: chat.currentSessionId 
          });
          const aiResponse = chatData.response;
          
          // 3. Get TTS Audio
          const cleanText = removeMarkdown(aiResponse).slice(0, 400);
          const { data: ttsData } = await api.post('/tts/speak', { 
            text: cleanText, 
            voice: "onyx", 
            speed: 1.15 
          });
          
          // Save to history
          chat.setMessages(prev => [
            ...prev,
            { id: Date.now(), role: 'user', content: userText },
            { id: Date.now() + 1, role: 'assistant', content: aiResponse }
          ]);
          
          setVoiceProcessing(false);
          setVoiceSpeaking(true);
          
          // 4. Play audio using AudioContext (works on mobile!)
          await playAudioBase64(ttsData.audio_base64);
          
          setVoiceSpeaking(false);
          
        } catch (err) {
          console.error("Voice error:", err);
          setVoiceProcessing(false);
          setVoiceSpeaking(false);
        }
      };
      
      mediaRecorder.start();
      setVoiceRecording(true);
      
      // Voice Activity Detection
      try {
        const vadContext = new AudioContext();
        const source = vadContext.createMediaStreamSource(stream);
        const analyser = vadContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let silenceStart = null;
        let hasSpoken = false;
        
        const checkAudio = () => {
          if (!voiceMediaRecorderRef.current || voiceMediaRecorderRef.current.state !== 'recording') {
            vadContext.close();
            return;
          }
          
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
          
          if (avg > 20) {
            hasSpoken = true;
            silenceStart = null;
          } else if (hasSpoken && !silenceStart) {
            silenceStart = Date.now();
          }
          
          if (silenceStart && Date.now() - silenceStart > 1200) {
            voiceMediaRecorderRef.current?.stop();
            vadContext.close();
            return;
          }
          
          requestAnimationFrame(checkAudio);
        };
        checkAudio();
      } catch (e) {
        setTimeout(() => {
          if (voiceMediaRecorderRef.current?.state === 'recording') {
            voiceMediaRecorderRef.current.stop();
          }
        }, 6000);
      }
      
    } catch (err) {
      console.error("Mic error:", err);
      toast.error("لا يمكن الوصول للميكروفون");
      setVoiceRecording(false);
    }
  }, [showVoiceCall, chat, ensureAudioContext, playAudioBase64]);

  const startVoiceCall = useCallback(() => {
    ensureAudioContext(); // Enable audio on user click
    setShowVoiceCall(true);
  }, [ensureAudioContext]);

  const endVoiceCall = useCallback(() => {
    setShowVoiceCall(false);
    setVoiceRecording(false);
    setVoiceProcessing(false);
    setVoiceSpeaking(false);
    
    if (voiceMediaRecorderRef.current?.state === 'recording') {
      try { voiceMediaRecorderRef.current.stop(); } catch (e) { console.warn('Stop recording error:', e); }
    }
    voiceMediaRecorderRef.current = null;
    
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  }, []);

  // Auto-start recording when voice call opens or after AI finishes
  useEffect(() => {
    if (showVoiceCall && !voiceRecording && !voiceProcessing && !voiceSpeaking) {
      const timer = setTimeout(startVoiceRecording, 400);
      return () => clearTimeout(timer);
    }
  }, [showVoiceCall, voiceRecording, voiceProcessing, voiceSpeaking, startVoiceRecording]);

  // Play message audio
  const handleSpeak = useCallback(async (messageId, text) => {
    if (speakingId === messageId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setSpeakingId(null);
      return;
    }

    if (audioRef.current) audioRef.current.pause();

    setSpeakingId(messageId);
    try {
      const cleanText = removeMarkdown(text);
      const response = await api.post('/tts/speak', { text: cleanText, voice: "alloy", speed: 1.0 });
      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setSpeakingId(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        setSpeakingId(null);
        audioRef.current = null;
      };
      
      await audio.play();
    } catch {
      toast.error("خطأ في تشغيل الصوت");
      setSpeakingId(null);
    }
  }, [speakingId]);

  // ============ Effects ============
  
  useEffect(() => {
    const assistantId = searchParams.get("assistant");
    if (assistantId) chat.loadAssistant(assistantId);
  }, [searchParams]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (sessionId) {
      chat.setCurrentSessionId(sessionId);
      chat.fetchHistory(sessionId);
    } else {
      chat.resetSession();
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.messages]);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  // ============ Handlers ============

  const handleSend = useCallback(async () => {
    if (attachments.selectedImage) {
      const imgBase64 = attachments.imageBase64;
      const imgPreview = attachments.imagePreview;
      const text = input.trim();
      attachments.clearAll();
      setInput('');
      await chat.sendImageMessage(imgBase64, imgPreview, text);
    } else if (attachments.selectedFile) {
      const reader = new FileReader();
      const file = attachments.selectedFile;
      const text = input.trim();
      reader.onloadend = async () => {
        attachments.clearAll();
        setInput('');
        await chat.sendFileMessage(reader.result.split(',')[1], file.name, text);
      };
      reader.readAsDataURL(file);
    } else if (input.trim()) {
      const text = input.trim();
      setInput('');
      await chat.sendMessage(text);
    }
  }, [attachments, input, chat]);

  const sidebarWidth = sidebarCollapsed ? '72px' : '280px';
  const canSend = input.trim() || attachments.selectedImage || attachments.selectedFile;

  return (
    <div className="chat-container h-screen bg-background flex flex-col" data-testid="chat-page">
      {/* Voice Call Screen */}
      <VoiceCallScreen
        isOpen={showVoiceCall}
        onClose={endVoiceCall}
        isRecording={voiceRecording}
        isSpeaking={voiceSpeaking}
        isProcessing={voiceProcessing}
        t={t}
      />
      
      {/* Header Spacer */}
      <div className="md:hidden h-16 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0)' }} />
      
      <AssistantIndicator assistant={chat.currentAssistant} isRTL={isRTL} onClose={chat.closeAssistant} />

      {/* Hidden Inputs */}
      <input type="file" id="file-input" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={(e) => attachments.handleFileSelect(e, 'file')} />
      <input type="file" id="image-input" ref={imageInputRef} className="hidden" accept="image/*" onChange={(e) => attachments.handleFileSelect(e, 'image')} />
      <input type="file" id="camera-input" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={(e) => attachments.handleFileSelect(e, 'camera')} />

      {/* Messages */}
      <ScrollArea className="flex-1 overflow-auto" style={{ paddingBottom: '90px' }}>
        <div className="w-full px-4 md:px-6 py-4">
          {chat.messages.length === 0 ? (
            <EmptyState t={t} />
          ) : (
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {chat.messages.map((msg, i) => (
                  <MessageItem key={msg.id || i} message={msg} index={i} onSpeak={handleSpeak} speakingId={speakingId} t={t} />
                ))}
              </AnimatePresence>
              <AnimatePresence>{chat.loading && <TypingIndicator />}</AnimatePresence>
              <div ref={messagesEndRef} className="h-4" />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div style={{ 
        position: 'fixed', bottom: 0, right: isMobile ? 0 : sidebarWidth, left: 0, zIndex: 40,
        padding: isMobile ? '8px' : '12px 20px', paddingRight: isMobile ? '8px' : '24px',
        paddingBottom: isMobile ? 'calc(70px + env(safe-area-inset-bottom, 0))' : '12px',
        backgroundColor: 'hsl(var(--background))', transition: 'right 0.3s ease'
      }}>
        <AnimatePresence>
          <ImagePreview imagePreview={attachments.imagePreview} onClear={attachments.clearImage} />
          <FilePreview file={attachments.selectedFile} onClear={attachments.clearFile} />
        </AnimatePresence>

        <AnimatePresence>
          {showAttachMenu && <AttachmentMenu onClose={() => setShowAttachMenu(false)} t={t} />}
        </AnimatePresence>

        <motion.div 
          className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-secondary/50 max-w-full"
          style={{ flexDirection: 'row-reverse' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.button
            type="button"
            disabled={!canSend || chat.loading}
            onClick={handleSend}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0 disabled:opacity-40"
            data-testid="send-btn"
          >
            <SendButtonLogo size={38} loading={chat.loading} />
          </motion.button>
          
          <input
            ref={chat.inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder={attachments.selectedImage ? "أضف وصفاً للصورة..." : attachments.selectedFile ? "أضف سؤالاً عن الملف..." : t("typeMessage")}
            className="flex-1 bg-transparent border-none outline-none px-3 py-2 text-foreground text-sm min-w-0"
            style={{ textAlign: 'right' }}
            data-testid="chat-input"
            dir="auto"
            autoComplete="off"
          />

          {/* زر المايكروفون */}
          <motion.button 
            type="button" 
            onClick={toggleRecording} 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className={cn(
              "flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors",
              isRecording ? "bg-red-500 text-white animate-pulse" : "bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground"
            )}
            data-testid="mic-btn"
          >
            <Mic className="w-4 h-4" />
          </motion.button>

          <motion.button 
            type="button" 
            onClick={() => setShowAttachMenu(!showAttachMenu)} 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className={cn(
              "flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors",
              showAttachMenu ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground"
            )}
            data-testid="attach-btn"
          >
            {showAttachMenu ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </motion.button>

          {/* زر المكالمة الصوتية */}
          <motion.button 
            type="button" 
            onClick={startVoiceCall} 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors bg-green-600 hover:bg-green-500 text-white"
            data-testid="voice-call-btn"
            title="مكالمة صوتية"
          >
            <Phone className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}
